import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, Plus, Minus, Send, Coffee, LogOut, RotateCcw, ChevronDown, ChevronUp } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const OrderForm = () => {
  const navigate = useNavigate();
  const [franchise, setFranchise] = useState(null);
  const [rawMaterials, setRawMaterials] = useState([]);
  const [loading, setLoading] = useState(false);
  // Store quantities for each product: { productName: { variant: quantity } }
  const [productQuantities, setProductQuantities] = useState({});
  // Store collapsed state for each category
  const [collapsedCategories, setCollapsedCategories] = useState({});
  // Additional requirements text
  const [additionalRequirements, setAdditionalRequirements] = useState('');

  useEffect(() => {
    // Check if franchise is logged in
    const storedFranchise = sessionStorage.getItem('franchise');
    if (!storedFranchise) {
      toast.error('Please login first');
      navigate('/franchise-login');
      return;
    }
    
    setFranchise(JSON.parse(storedFranchise));
    fetchRawMaterials();
  }, [navigate]);

  const fetchRawMaterials = async () => {
    try {
      const storedFranchise = JSON.parse(sessionStorage.getItem('franchise'));
      const params = {};
      
      if (storedFranchise) {
        params.franchise_id = storedFranchise.id;
        params.franchise_model = storedFranchise.franchise_model || 'Kiosk';
        params.is_branch = storedFranchise.is_branch || false;
      }
      
      const response = await axios.get(`${API}/raw-materials`, { params });
      setRawMaterials(response.data);
    } catch (error) {
      console.error('Error fetching raw materials:', error);
      toast.error('Failed to load raw materials');
    }
  };

  const handleLogout = () => {
    sessionStorage.removeItem('franchise');
    toast.success('Logged out successfully');
    navigate('/');
  };

  const toggleCategory = (category) => {
    setCollapsedCategories(prev => ({
      ...prev,
      [category]: !prev[category]
    }));
  };

  const groupMaterialsByCategory = () => {
    const grouped = {};
    rawMaterials.forEach(material => {
      const category = material.category || 'Category 1';
      if (!grouped[category]) {
        grouped[category] = [];
      }
      grouped[category].push(material);
    });
    return grouped;
  };

  const increaseQuantity = (productName, variant) => {
    setProductQuantities(prev => ({
      ...prev,
      [productName]: {
        ...(prev[productName] || {}),
        [variant]: ((prev[productName]?.[variant]) || 0) + 1
      }
    }));
  };

  const decreaseQuantity = (productName, variant) => {
    setProductQuantities(prev => {
      const currentQty = prev[productName]?.[variant] || 0;
      if (currentQty <= 0) return prev;
      
      return {
        ...prev,
        [productName]: {
          ...(prev[productName] || {}),
          [variant]: currentQty - 1
        }
      };
    });
  };

  const getQuantity = (productName, variant) => {
    return productQuantities[productName]?.[variant] || 0;
  };

  const clearAll = () => {
    setProductQuantities({});
    toast.info('All quantities reset');
  };

  const getVariantPrice = (material, variant) => {
    // Use price_map if available, otherwise fallback to price_per_unit
    if (material.price_map && material.price_map[variant]) {
      return material.price_map[variant];
    }
    return material.price_per_unit || 0;
  };

  const calculateProductTotal = (material, variant) => {
    const quantity = getQuantity(material.name, variant);
    const price = getVariantPrice(material, variant);
    return quantity * price;
  };

  const calculateGrandTotal = () => {
    let total = 0;
    rawMaterials.forEach(material => {
      if (material.options && material.options.length > 0) {
        material.options.forEach(variant => {
          total += calculateProductTotal(material, variant);
        });
      } else {
        total += calculateProductTotal(material, 'default');
      }
    });
    return total;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!franchise) {
      toast.error('Please login first');
      navigate('/franchise-login');
      return;
    }
    
    // Build order items from productQuantities
    const orderItems = [];
    rawMaterials.forEach(material => {
      if (material.options && material.options.length > 0) {
        material.options.forEach(variant => {
          const quantity = getQuantity(material.name, variant);
          if (quantity > 0) {
            const variantPrice = getVariantPrice(material, variant);
            orderItems.push({
              item_name: material.name,
              size_or_quantity: variant,
              quantity: quantity,
              price_per_unit: variantPrice,
              total_price: quantity * variantPrice
            });
          }
        });
      } else {
        const quantity = getQuantity(material.name, 'default');
        if (quantity > 0) {
          const price = getVariantPrice(material, 'default');
          orderItems.push({
            item_name: material.name,
            size_or_quantity: material.type || 'N/A',
            quantity: quantity,
            price_per_unit: price,
            total_price: quantity * price
          });
        }
      }
    });
    
    if (orderItems.length === 0) {
      toast.error('Please add at least one item with quantity > 0');
      return;
    }
    
    setLoading(true);
    
    try {
      const orderData = {
        franchise_id: franchise.id,  // Include franchise ID
        franchise_name: franchise.name,
        location: franchise.location,
        contact_number: franchise.contact,
        ordered_items: orderItems,
        additional_requirements: additionalRequirements.trim(),
      };
      
      const response = await axios.post(`${API}/orders`, orderData);
      
      // Open PDF in new tab (non-blocking approach)
      if (response.data && response.data.pdf_url) {
        // Open PDF in new tab immediately - doesn't block anything
        window.open(response.data.pdf_url, '_blank');
        
        // Display success message with Order ID
        const successMsg = response.data.order_id 
          ? `Order ${response.data.order_id} submitted successfully! PDF opened in new tab.`
          : 'Order submitted! PDF opened in new tab.';
        toast.success(successMsg, { duration: 5000 });
      } else {
        const successMsg = response.data.order_id 
          ? `Order ${response.data.order_id} submitted successfully! Email notification sent.`
          : 'Order submitted successfully! Email notification sent.';
        toast.success(successMsg, { duration: 5000 });
      }
      
      // Navigate to payment confirmation with order details
      navigate('/payment-confirmation', {
        state: {
          orderDetails: {
            id: response.data.order_id || (orderData.franchise_name + '-' + Date.now()),
            order_id: response.data.order_id,
            franchise_name: franchise.name,
            ordered_items: orderItems,
            total_amount: calculateGrandTotal()
          }
        }
      });
    } catch (error) {
      console.error('Error submitting order:', error);
      toast.error(error.response?.data?.detail || 'Failed to submit order');
    } finally {
      setLoading(false);
    }
  };

  if (!franchise) {
    return (
      <div style={styles.container}>
        <div style={styles.loadingCard}>
          <p>Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <>
      {/* Full Screen Loader */}
      {loading && (
        <div style={styles.loaderOverlay}>
          <div style={styles.loaderContent}>
            <div style={styles.loaderLogoContainer}>
              <img 
                src="/assets/logo.png" 
                alt="Prakash Coffee Works" 
                style={styles.loaderLogo} 
              />
              <div style={styles.loaderSpinner}></div>
            </div>
            <p style={styles.loaderText}>Submitting your order...</p>
            <p style={styles.loaderSubtext}>Please wait, do not close this window</p>
          </div>
        </div>
      )}

      <div style={styles.container}>
        <div style={styles.header}>
          <button
            data-testid="back-btn"
            onClick={() => navigate('/')}
            style={styles.backButton}
          >
            <ArrowLeft size={20} />
          </button>
          <div style={styles.headerContent}>
            <img src="/assets/logo.png" alt="Prakash Coffee Works" style={styles.headerLogo} />
            <h1 style={styles.headerTitle}>Place Your Order</h1>
          </div>
          <button
            data-testid="logout-btn"
            onClick={handleLogout}
            style={styles.logoutButton}
          >
            <LogOut size={20} />
          </button>
        </div>

      <div className="card" style={styles.formCard}>
        {/* Franchise Info - Read Only */}
        <div style={styles.franchiseInfo}>
          <h3 style={styles.franchiseInfoTitle}>Logged in as:</h3>
          <div style={styles.franchiseDetails}>
            <p style={styles.franchiseName}>{franchise.name}</p>
            <p style={styles.franchiseLocation}>{franchise.location}</p>
            <p style={styles.franchiseContact}>{franchise.contact}</p>
          </div>
        </div>

        <form onSubmit={handleSubmit}>
          {/* Header with Clear All Button */}
          <div style={styles.orderHeader}>
            <h3 style={styles.orderTitle}>Select Products</h3>
            <button
              type="button"
              data-testid="clear-all-btn"
              onClick={clearAll}
              style={styles.clearButton}
            >
              <RotateCcw size={16} />
              Clear All
            </button>
          </div>

          {/* Products List - Grouped by Category */}
          <div style={styles.productsList}>
            {Object.entries(groupMaterialsByCategory()).map(([category, materials], categoryIdx) => (
              <div key={category} style={styles.categorySection}>
                {/* Category Header */}
                <div 
                  style={styles.categoryHeader}
                  onClick={() => toggleCategory(category)}
                  data-testid={`category-${categoryIdx}`}
                >
                  <h3 style={styles.categoryTitle}>{category}</h3>
                  {collapsedCategories[category] ? <ChevronDown size={20} /> : <ChevronUp size={20} />}
                </div>

                {/* Products in Category */}
                {!collapsedCategories[category] && (
                  <div style={styles.categoryProducts}>
                    {materials.map((material, idx) => {
                      // For materials with options/variants
                      if (material.options && material.options.length > 0) {
                        return material.options.map((variant, variantIdx) => {
                          const quantity = getQuantity(material.name, variant);
                          const total = calculateProductTotal(material, variant);
                          
                          return (
                            <div 
                              key={`${categoryIdx}-${idx}-${variantIdx}`} 
                              style={styles.productCard}
                              data-testid={`product-${categoryIdx}-${idx}-${variantIdx}`}
                            >
                              <div style={styles.productInfo}>
                                <h4 style={styles.productName}>{material.name}</h4>
                                {variant && <p style={styles.productVariant}>{variant}</p>}
                              </div>
                              
                              <div style={styles.productControls}>
                                <div style={styles.quantityControls}>
                                  <button
                                    type="button"
                                    data-testid={`decrease-${categoryIdx}-${idx}-${variantIdx}`}
                                    onClick={() => decreaseQuantity(material.name, variant)}
                                    style={styles.quantityButton}
                                    disabled={quantity === 0}
                                  >
                                    <Minus size={18} />
                                  </button>
                                  <span style={styles.quantityDisplay} data-testid={`quantity-${categoryIdx}-${idx}-${variantIdx}`}>
                                    {quantity}
                                  </span>
                                  <button
                                    type="button"
                                    data-testid={`increase-${categoryIdx}-${idx}-${variantIdx}`}
                                    onClick={() => increaseQuantity(material.name, variant)}
                                    style={styles.quantityButton}
                                  >
                                    <Plus size={18} />
                                  </button>
                                </div>
                                
                                <div style={styles.productTotal}>
                                  <span style={styles.totalLabel}>Total:</span>
                                  <span style={styles.totalAmount} data-testid={`total-${categoryIdx}-${idx}-${variantIdx}`}>
                                    ₹{total.toFixed(2)}
                                  </span>
                                </div>
                              </div>
                            </div>
                          );
                        });
                      } else {
                        // For materials without variants
                        const quantity = getQuantity(material.name, 'default');
                        const total = calculateProductTotal(material, 'default');
                        
                        return (
                          <div 
                            key={`${categoryIdx}-${idx}`} 
                            style={styles.productCard}
                            data-testid={`product-${categoryIdx}-${idx}`}
                          >
                            <div style={styles.productInfo}>
                              <h4 style={styles.productName}>{material.name}</h4>
                            </div>
                            
                            <div style={styles.productControls}>
                              <div style={styles.quantityControls}>
                                <button
                                  type="button"
                                  data-testid={`decrease-${categoryIdx}-${idx}`}
                                  onClick={() => decreaseQuantity(material.name, 'default')}
                                  style={styles.quantityButton}
                                  disabled={quantity === 0}
                                >
                                  <Minus size={18} />
                                </button>
                                <span style={styles.quantityDisplay} data-testid={`quantity-${categoryIdx}-${idx}`}>
                                  {quantity}
                                </span>
                                <button
                                  type="button"
                                  data-testid={`increase-${categoryIdx}-${idx}`}
                                  onClick={() => increaseQuantity(material.name, 'default')}
                                  style={styles.quantityButton}
                                >
                                  <Plus size={18} />
                                </button>
                              </div>
                              
                              <div style={styles.productTotal}>
                                <span style={styles.totalLabel}>Total:</span>
                                <span style={styles.totalAmount} data-testid={`total-${categoryIdx}-${idx}`}>
                                  ₹{total.toFixed(2)}
                                </span>
                              </div>
                            </div>
                          </div>
                        );
                      }
                    })}
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* Grand Total */}
          <div style={styles.grandTotalCard}>
            <span style={styles.grandTotalLabel}>Order Total:</span>
            <span style={styles.grandTotalAmount} data-testid="grand-total">
              ₹{calculateGrandTotal().toFixed(2)}
            </span>
          </div>

          {/* Submit Button */}
          {/* Additional Requirements Section */}
          <div style={styles.additionalSection}>
            <h3 style={styles.additionalTitle}>Additional Requirements (Optional)</h3>
            <p style={styles.additionalSubtitle}>
              Request any additional items or specify special requirements not listed above
            </p>
            <textarea
              value={additionalRequirements}
              onChange={(e) => setAdditionalRequirements(e.target.value)}
              placeholder="Example: Need 2 extra spoons, additional packaging materials, etc."
              style={styles.additionalTextarea}
              rows="4"
            />
          </div>

          <button
            type="submit"
            data-testid="submit-order-btn"
            className="btn-primary"
            disabled={loading || calculateGrandTotal() === 0}
            style={{ 
              ...styles.submitButton, 
              opacity: (loading || calculateGrandTotal() === 0) ? 0.5 : 1 
            }}
          >
            <Send size={18} style={{ marginRight: '8px' }} />
            Submit Order
          </button>
        </form>
      </div>
    </div>
    </>
  );
};

const styles = {
  container: {
    minHeight: '100vh',
    padding: '20px',
    paddingBottom: '80px',
  },
  header: {
    maxWidth: '900px',
    margin: '0 auto 32px',
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
  },
  backButton: {
    background: 'white',
    border: 'none',
    borderRadius: '8px',
    padding: '12px',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
    transition: 'transform 0.2s',
  },
  logoutButton: {
    background: '#dc2626',
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    padding: '12px',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
    transition: 'transform 0.2s',
  },
  headerContent: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    flex: 1,
  },
  headerLogo: {
    width: '48px',
    height: '48px',
    objectFit: 'contain',
  },
  headerTitle: {
    fontSize: '28px',
    color: '#6b4423',
    fontFamily: "'League Spartan', sans-serif",
    fontWeight: '700',
  },
  formCard: {
    maxWidth: '900px',
    margin: '0 auto',
  },
  franchiseInfo: {
    background: 'linear-gradient(135deg, #6b4423 0%, #8b5e34 100%)',
    color: 'white',
    borderRadius: '12px',
    padding: '24px',
    marginBottom: '32px',
  },
  franchiseInfoTitle: {
    fontSize: '14px',
    opacity: 0.9,
    marginBottom: '12px',
    fontWeight: '500',
  },
  franchiseDetails: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
  },
  franchiseName: {
    fontSize: '20px',
    fontWeight: '700',
    marginBottom: '4px',
  },
  franchiseLocation: {
    fontSize: '16px',
    opacity: 0.9,
  },
  franchiseContact: {
    fontSize: '14px',
    opacity: 0.8,
  },
  loadingCard: {
    maxWidth: '400px',
    margin: '100px auto',
    padding: '32px',
    textAlign: 'center',
    background: 'white',
    borderRadius: '16px',
    boxShadow: '0 4px 24px rgba(0, 0, 0, 0.08)',
  },
  orderHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '24px',
  },
  orderTitle: {
    fontSize: '20px',
    color: '#6b4423',
    fontWeight: '600',
  },
  clearButton: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    background: 'white',
    color: '#6b4423',
    border: '2px solid #e8dcc8',
    borderRadius: '8px',
    padding: '8px 16px',
    cursor: 'pointer',
    fontSize: '14px',
    fontWeight: '600',
    transition: 'all 0.2s',
  },
  productsList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '24px',
    marginBottom: '24px',
  },
  categorySection: {
    background: 'white',
    borderRadius: '12px',
    overflow: 'hidden',
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
  },
  categoryHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '16px 20px',
    background: 'linear-gradient(135deg, #6b4423 0%, #8b5e34 100%)',
    color: 'white',
    cursor: 'pointer',
    userSelect: 'none',
  },
  categoryTitle: {
    fontSize: '18px',
    fontWeight: '700',
    margin: 0,
  },
  categoryProducts: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
    padding: '16px',
  },
  productCard: {
    background: '#f9f6f1',
    borderRadius: '12px',
    padding: 'clamp(16px, 3vw, 20px)',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: 'clamp(12px, 2vw, 20px)',
    flexWrap: 'wrap',
  },
  productInfo: {
    flex: '1',
    minWidth: 'clamp(180px, 40vw, 200px)',
  },
  productName: {
    fontSize: '18px',
    fontWeight: '700',
    color: '#6b4423',
    marginBottom: '4px',
  },
  productVariant: {
    fontSize: '14px',
    color: '#8b5e34',
    marginBottom: '8px',
    fontWeight: '600',
  },
  productType: {
    fontSize: '14px',
    color: '#8b5e34',
    marginBottom: '8px',
  },
  productPrice: {
    fontSize: '14px',
    color: '#6b5d54',
  },
  productControls: {
    display: 'flex',
    alignItems: 'center',
    gap: '24px',
  },
  quantityControls: {
    display: 'flex',
    alignItems: 'center',
    gap: 'clamp(8px, 2vw, 12px)',
    flexWrap: 'wrap',
    background: 'white',
    borderRadius: '8px',
    padding: '8px',
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
  },
  quantityButton: {
    minWidth: '44px',
    minHeight: '44px',
    width: 'clamp(40px, 8vw, 44px)',
    height: 'clamp(40px, 8vw, 44px)',
    background: '#6b4423',
    color: 'white',
    border: 'none',
    borderRadius: '6px',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'all 0.2s',
  },
  quantityDisplay: {
    fontSize: '18px',
    fontWeight: '700',
    color: '#6b4423',
    minWidth: '40px',
    textAlign: 'center',
  },
  productTotal: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-end',
    minWidth: '100px',
  },
  totalLabel: {
    fontSize: '12px',
    color: '#8b5e34',
    marginBottom: '4px',
  },
  totalAmount: {
    fontSize: '20px',
    fontWeight: '700',
    color: '#6b4423',
  },
  grandTotalCard: {
    background: 'linear-gradient(135deg, #6b4423 0%, #8b5e34 100%)',
    color: 'white',
    borderRadius: '12px',
    padding: '24px',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '24px',
  },
  grandTotalLabel: {
    fontSize: '20px',
    fontWeight: '600',
  },
  grandTotalAmount: {
    fontSize: '32px',
    fontWeight: '700',
  },
  submitButton: {
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  additionalSection: {
    background: '#fff8e8',
    borderRadius: '12px',
    padding: '24px',
    marginBottom: '24px',
    border: '1px solid #e8dcc8',
  },
  additionalTitle: {
    fontSize: '18px',
    color: '#6b4423',
    marginBottom: '8px',
    fontFamily: "'League Spartan', sans-serif",
    fontWeight: '600',
  },
  additionalSubtitle: {
    fontSize: '14px',
    color: '#8b5e34',
    marginBottom: '16px',
    lineHeight: '1.5',
  },
  additionalTextarea: {
    width: '100%',
    padding: '12px',
    fontSize: '14px',
    border: '2px solid #e8dcc8',
    borderRadius: '8px',
    fontFamily: "'League Spartan', sans-serif",
    resize: 'vertical',
    background: 'white',
    color: '#6b4423',
    lineHeight: '1.5',
  },
  // Full Screen Loader Styles
  loaderOverlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.85)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 9999,
    backdropFilter: 'blur(4px)',
  },
  loaderContent: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '24px',
  },
  loaderLogoContainer: {
    position: 'relative',
    width: '150px',
    height: '150px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  loaderLogo: {
    width: '100px',
    height: '100px',
    objectFit: 'contain',
    zIndex: 1,
    animation: 'pulse 2s ease-in-out infinite',
  },
  loaderSpinner: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    border: '4px solid rgba(255, 255, 255, 0.1)',
    borderTop: '4px solid #ffffff',
    borderRadius: '50%',
    animation: 'spin 1s linear infinite',
  },
  loaderText: {
    color: '#ffffff',
    fontSize: '24px',
    fontWeight: '600',
    fontFamily: "'League Spartan', sans-serif",
    margin: 0,
    textAlign: 'center',
  },
  loaderSubtext: {
    color: 'rgba(255, 255, 255, 0.7)',
    fontSize: '14px',
    fontFamily: "'League Spartan', sans-serif",
    margin: 0,
    textAlign: 'center',
  },
};

export default OrderForm;